import type { IntakeSummaryPdfData } from '@common/types/formSubmissions';
import {
    buildIntakeSummaryData,
    type IntakeSummaryContacts,
    type IntakeSummaryItem,
    type IntakeSummaryRow,
    type IntakeSummaryTable,
} from '@common/utils/intakeSummary';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { Button, Paper, Stack, Typography } from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import type { MouseEvent } from 'react';
import { useMemo, useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import EditableViewComponentLeftAligned from '@/components/ReadOnlyComponent/EditableViewComponentLeftAligned';
import RenderCriticalQuestion from '@/components/RenderCriticalQuestion';
import { uidRegex } from '@/constants';
import { getFileDetails } from '@/services/FileService';
import { downloadIntakeSummaryPdf } from '@/services/ProspectService';
import { errorSnackbar } from '@/utils/snackbar';
import DraftIntakeDisclaimer from '../DraftIntakeDisclaimer';
import IntakeResponseTable from './IntakeResponseTable';

const NOT_ANSWERED = 'Not Answered';

function triggerDownload(url: string, filename: string) {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

function getOnFileLinkClickHandler(fileId: string, fileName: string) {
    return (event: MouseEvent) => {
        event.preventDefault();
        downloadFile(fileId, fileName || 'document');
    };
}

async function downloadFile(fileId: string, filename: string) {
    const response = await fetch(`${import.meta.env.VITE_APP_API_URL}/api/v1/files/${fileId}`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    triggerDownload(url, filename);
    URL.revokeObjectURL(url);
}

function getItemFileIds(items: IntakeSummaryItem[]): string[] {
    return items.flatMap((item) => {
        if (item.kind === 'rows') return item.rows.flatMap((row) => row.fileIds ?? []);
        if (item.kind === 'contacts') {
            return item.contacts.entries.flatMap((entry) => getItemFileIds(entry.items));
        }
        return [];
    });
}

function SummaryRow({
    row,
    fileNamesById,
}: {
    row: IntakeSummaryRow;
    fileNamesById: Record<string, string>;
}) {
    const label = (
        <RenderCriticalQuestion title={row.label} isCritical={row.isCriticalQuestion ?? false} />
    );

    if (row.fileIds && row.fileIds.length > 0) {
        return row.fileIds.map((fileId) => {
            const fileName = fileNamesById[fileId] ?? fileId;
            return (
                <EditableViewComponentLeftAligned
                    key={fileId}
                    label={label}
                    value={fileName}
                    isCopyable={false}
                    link="#"
                    onLinkClick={getOnFileLinkClickHandler(fileId, fileName)}
                />
            );
        });
    }

    return (
        <EditableViewComponentLeftAligned
            label={label}
            value={row.value || NOT_ANSWERED}
            isCopyable={!!row.value}
        />
    );
}

function SummaryTable({ table }: { table: IntakeSummaryTable }) {
    if (table.rows.length === 0) {
        return (
            <SummaryRow
                row={{
                    label: table.title,
                    value: '',
                    isCriticalQuestion: table.isCriticalQuestion,
                }}
                fileNamesById={{}}
            />
        );
    }

    return (
        <IntakeResponseTable
            title={table.title}
            isCriticalQuestion={table.isCriticalQuestion ?? false}
            columns={table.columns.map((label) => ({ label }))}
            rows={table.rows}
        />
    );
}

function SummaryContacts({
    contacts,
    fileNamesById,
}: {
    contacts: IntakeSummaryContacts;
    fileNamesById: Record<string, string>;
}) {
    // A titled contact block nests its entries under the title; clients are the section itself
    const entries = contacts.entries.map((entry, index) => (
        <Stack
            key={`${entry.title ?? ''}-${index}`}
            gap="12px"
            paddingLeft={contacts.title ? '16px' : 0}
        >
            {entry.title && (
                <Typography variant={contacts.title ? 'labelMedium' : 'labelLarge'}>
                    {entry.title}
                </Typography>
            )}
            <SummaryItems items={entry.items} fileNamesById={fileNamesById} />
        </Stack>
    ));

    if (!contacts.title) {
        return entries;
    }

    return (
        <Stack gap="16px" width="100%">
            <Typography variant="labelLarge">
                <RenderCriticalQuestion
                    title={contacts.title}
                    isCritical={contacts.isCriticalQuestion ?? false}
                />
            </Typography>
            {entries}
        </Stack>
    );
}

function SummaryItems({
    items,
    fileNamesById,
}: {
    items: IntakeSummaryItem[];
    fileNamesById: Record<string, string>;
}) {
    return items.map((item, index) => {
        switch (item.kind) {
            case 'rows':
                return item.rows.map((row, rowIndex) => (
                    <SummaryRow
                        key={`${index}-${rowIndex}`}
                        row={row}
                        fileNamesById={fileNamesById}
                    />
                ));
            case 'table':
                return <SummaryTable key={index} table={item.table} />;
            case 'contacts':
                return (
                    <SummaryContacts
                        key={index}
                        contacts={item.contacts}
                        fileNamesById={fileNamesById}
                    />
                );
            // The screen lists contacts as rows, so it never builds contact grids
            case 'contactGrid':
                return null;
        }
    });
}

interface Props extends IntakeSummaryPdfData {
    isDraft?: boolean;
    draftLastUpdatedAt?: Date | string;
}

/** The intake summary as the prospect screen shows it, matching the summary PDF */
export default function IntakeResponseInformationView({
    matterType,
    formSubmissionFields,
    contactFormValues,
    showEmptyAnswersInIntakeSummary = true,
    showContactsAsColumnsInIntakeSummary = false,
    isDraft = false,
    draftLastUpdatedAt,
}: Props) {
    const [isIntakePdfDownloading, setIsIntakePdfDownloading] = useState(false);

    // Kept stable so file name lookups don't rebuild the summary as they resolve
    const summary = useMemo(
        () =>
            buildIntakeSummaryData(
                { matterType, formSubmissionFields, contactFormValues },
                {
                    showEmptyAnswersInIntakeSummary,
                    // The screen is wide enough to list contacts as rows
                    showContactsAsColumnsInIntakeSummary: false,
                    includeUploads: true,
                },
            ),
        [matterType, formSubmissionFields, contactFormValues, showEmptyAnswersInIntakeSummary],
    );

    const fileIds = useMemo(
        () => [
            ...new Set(
                summary.sections
                    .flatMap((section) => getItemFileIds(section.items))
                    .filter((fileId) => uidRegex.test(fileId)),
            ),
        ],
        [summary],
    );

    const fileQueries = useQueries({
        queries: fileIds.map((fileId) => ({
            queryKey: ['fileDetails', fileId],
            queryFn: async () => {
                const response = await getFileDetails(fileId);
                return response.data;
            },
            staleTime: 5 * 60_000,
        })),
    });

    const fileNamesById = fileIds.reduce<Record<string, string>>((fileNameMap, fileId, index) => {
        const fileName = fileQueries[index]?.data?.fileName;
        if (fileName) {
            fileNameMap[fileId] = fileName;
        }
        return fileNameMap;
    }, {});

    const handlePrintClick = async () => {
        setIsIntakePdfDownloading(true);
        const filename = `intake_${contactFormValues[0]?.lastName || 'summary'}.pdf`;

        try {
            const response = await downloadIntakeSummaryPdf({
                matterType,
                formSubmissionFields,
                contactFormValues,
                showEmptyAnswersInIntakeSummary,
                showContactsAsColumnsInIntakeSummary,
            });
            const url = URL.createObjectURL(response.data);
            triggerDownload(url, filename);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
            errorSnackbar('Error downloading the file');
        } finally {
            setIsIntakePdfDownloading(false);
        }
    };

    return (
        <Stack padding="24px" gap="16px">
            {isDraft && <DraftIntakeDisclaimer draftLastUpdatedAt={draftLastUpdatedAt} />}
            {summary.sections.map((section, index) => (
                <Paper
                    key={section.title}
                    sx={{
                        padding: '24px',
                        border: '1px solid',
                        borderColor: 'neutral.borderColor',
                        borderRadius: '8px',
                        boxShadow: 'none',
                        bgcolor: 'neutral.white',
                    }}
                >
                    <Stack gap="16px">
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h3">{section.title}</Typography>
                            {index === 0 && !isDraft && (
                                <Button variant="contained" onClick={handlePrintClick}>
                                    {isIntakePdfDownloading ? (
                                        <LoadingButton loading variant="outlined" />
                                    ) : (
                                        <PrintOutlinedIcon />
                                    )}
                                </Button>
                            )}
                        </Stack>
                        <SummaryItems items={section.items} fileNamesById={fileNamesById} />
                    </Stack>
                </Paper>
            ))}
        </Stack>
    );
}
